import React, { useState, useEffect } from "react";
import {
	Card,
	PaddedContainer,
	SPACING,
	genClassName,
} from "@disco/disco_core";
import { AnimatePresence, motion } from "framer-motion";

import "./lead-gen-v0.css";
import LeadGenHeader, {
	LeadGenHeaderFullScreenControls,
} from "./LeadGenHeader";
import LeadGenFooter from "./LeadGenFooter";
import useData from "../../hooks/useData";
import brandEventPropertyMapper from "../../utils/brandEventPropertyMapper";
import LeadGenProductRow from "./LeadGenProductRow";
import useLeadGenV0Config, {
	LEAD_GEN_FEATURE,
	SPACING_TARGET,
} from "../../hooks/useLeadGenV0Config";
import useExperience from "../../hooks/useExperience";
import useElementInView from "../../hooks/useElementInView";
import { HorizontalSeparator } from "../CoreUI";
import inPreview from "../../utils/inPreview";
import useMode from "../../hooks/useMode";
import LeadGenOfferSlot from "./LeadGenOfferSlot";
export const LeadGenSpacer = ({ spacingTarget = "", ...rest }) => {
	const { spacingConfig } = useLeadGenV0Config();

	if (
		!spacingConfig ||
		typeof spacingConfig !== "object" ||
		Object.entries(spacingConfig).length === 0
	) {
		console.error("[Disco]: Invalid spacing config found", spacingTarget);
		return null;
	}

	if (
		!(spacingTarget in SPACING_TARGET) ||
		typeof spacingTarget !== "string"
	) {
		console.error(
			"[Disco]: Invalid spacing target received",
			spacingTarget
		);
		return null;
	}

	const marginTop =
		spacingTarget.toLowerCase().indexOf("top") !== -1
			? spacingConfig[spacingTarget]
			: undefined;

	return <PaddedContainer marginTop={marginTop} {...rest} />;
};

const animationVariants = {
	initial: {
		opacity: 0,
	},
	animate: {
		opacity: 1,
		transition: {
			opacity: { duration: 0.3, ease: "easeInOut" },
		},
	},
	exit: {
		opacity: 0,
		transition: {
			opacity: { duration: 0.2, ease: "easeInOut" },
		},
	},
};

const LeadGenV0 = ({
	totalOffers,

	activeBrandIdx,
	activeBrand,
	claimedOffers,

	triggerWidgetResize,
	COMPUTED_PADDING,
}) => {
	const { containerRef } = useData();
	const [showOfferSlot, setShowOfferSlot] = useState(true);
	const [showProductRowHeading, setShowProductRowHeading] = useState(true);
	const isInitialMount = React.useRef(true);
	const { requestParentStats } = useElementInView();

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
		}
	}, []);

	const handleAnimationComplete = () => {
		setShowProductRowHeading(false);
		triggerWidgetResize();
		requestParentStats();
	};

	const { activeWidgetVersion } = useExperience();
	const { isInline } = useMode();

	const defaultEventProps = brandEventPropertyMapper({
		brand: activeBrand,
		idx: activeBrandIdx,
		feedHasUpsell: false,
	});

	const { shouldShowProductRow, isFeatureActive } = useLeadGenV0Config();

	const shouldShowHeaderBrandDivider = isFeatureActive({
		feature: LEAD_GEN_FEATURE.HEADING_BRAND_DIVIDER,
	});

	return (
		<Card
			noShadow
			className={genClassName({
				base: "lead-gen",
				conditionals: {
					[`lead-gen-v${activeWidgetVersion}`.replace(".", "-")]:
						activeWidgetVersion,
					"lead-gen-in-preview": inPreview,
				}, // TODO: Add more generalized support across versions / types for replacing "." with "-"
				additional: "lead-gen-v0",
			})}
			data-intersectionuid="lead-gen"
			data-testid="lead-gen-v0"
			vPadding={20}
			ref={containerRef}
		>
			<PaddedContainer hPadding={COMPUTED_PADDING}>
				{!isInline && (
					<LeadGenHeaderFullScreenControls
						activeBrand={activeBrand}
						activeBrandIdx={activeBrandIdx}
						defaultEventProps={defaultEventProps}
					/>
				)}
				<LeadGenHeader
					totalIdx={totalOffers}
					claimedCount={claimedOffers.length}
					triggerWidgetResize={triggerWidgetResize}
					showOfferSlot={showProductRowHeading}
				/>
			</PaddedContainer>
			{/* Spacer */}
			{showProductRowHeading && (
				<PaddedContainer marginTop={SPACING.SMALL} />
			)}
			{shouldShowHeaderBrandDivider && <HorizontalSeparator />}
			<AnimatePresence
				mode="wait"
				onExitComplete={handleAnimationComplete}
			>
				{showOfferSlot && (
					<motion.div
						key="lead-gen-offer-slot"
						initial={isInitialMount.current ? false : "initial"}
						animate="animate"
						exit="exit"
						variants={animationVariants}
					>
						<LeadGenOfferSlot
							hPadding={COMPUTED_PADDING}
							defaultEventProps={defaultEventProps}
							setShowOfferSlot={setShowOfferSlot}
							triggerWidgetResize={triggerWidgetResize}
						/>
					</motion.div>
				)}
			</AnimatePresence>
			{shouldShowProductRow && (
				<>
					{showOfferSlot && (
						<>
							<LeadGenSpacer
								spacingTarget={SPACING_TARGET.PRODUCT_ROW_TOP}
							/>
							<HorizontalSeparator />
						</>
					)}
					<LeadGenProductRow
						vPadding={SPACING.REGULAR}
						parentHPadding={COMPUTED_PADDING}
						showHeading={showProductRowHeading}
						tightTop={!showOfferSlot}
					/>
				</>
			)}
			<LeadGenFooter
				hPadding={COMPUTED_PADDING}
				marginTop={shouldShowProductRow ? undefined : SPACING.SMALL}
			/>
		</Card>
	);
};
export default LeadGenV0;
